import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Eraser, RotateCcw, Check, Palette, PenTool } from 'lucide-react';

const HandwritingCanvas = ({ 
  onSubmit, 
  prompt = "Write here...",
  expectedAnswer = null,
  width = 600,
  height = 300,
  showColorPicker = true,
  showSizeSelector = true
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState(null);
  const [strokeColor, setStrokeColor] = useState('#1E40AF');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [hasDrawn, setHasDrawn] = useState(false);

  const colors = [
    '#1E40AF', // Blue
    '#DC2626', // Red
    '#16A34A', // Green
    '#9333EA', // Purple
    '#EA580C', // Orange
    '#000000', // Black
  ];

  const sizes = [
    { name: 'Thin', value: 2 },
    { name: 'Medium', value: 4 },
    { name: 'Thick', value: 8 },
    { name: 'Bold', value: 12 },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      setContext(ctx);
      
      // Set white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw guide lines for writing
      drawGuideLines(ctx, canvas.width, canvas.height);
    }
  }, []);

  useEffect(() => {
    if (context) {
      context.strokeStyle = strokeColor;
      context.lineWidth = strokeWidth;
    }
  }, [strokeColor, strokeWidth, context]);

  const drawGuideLines = (ctx, w, h) => {
    ctx.save();
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 1;
    
    // Horizontal lines for writing guides
    const lineSpacing = 40;
    for (let y = lineSpacing; y < h; y += lineSpacing) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
      
      // Dashed midline
      if (y + lineSpacing / 2 < h) {
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(0, y + lineSpacing / 2);
        ctx.lineTo(w, y + lineSpacing / 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
    ctx.restore();
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches && e.touches[0]) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    context.beginPath();
    context.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    context.closePath();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, canvas.width, canvas.height);
    drawGuideLines(context, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSubmit = () => {
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL('image/png');
    if (onSubmit) {
      onSubmit(imageData);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        {/* Prompt */}
        <div className="mb-4 text-center">
          <p className="text-lg font-medium text-slate-700">{prompt}</p>
          {expectedAnswer && (
            <p className="text-sm text-slate-500 mt-1">
              Try to write: <span className="font-bold text-slate-700">{expectedAnswer}</span>
            </p>
          )}
        </div>

        {/* Tools */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          {/* Color Picker */}
          {showColorPicker && (
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-slate-400" />
              <div className="flex gap-1">
                {colors.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      strokeColor === color ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setStrokeColor(color)}
                    data-testid={`color-${color}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Size Selector */}
          {showSizeSelector && (
            <div className="flex items-center gap-2">
              <PenTool className="w-4 h-4 text-slate-400" />
              <div className="flex gap-1">
                {sizes.map((size) => (
                  <button
                    key={size.value}
                    className={`px-3 py-1 text-xs rounded-full transition-all ${
                      strokeWidth === size.value 
                        ? 'bg-slate-800 text-white' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => setStrokeWidth(size.value)}
                    data-testid={`size-${size.value}`}
                  >
                    {size.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearCanvas}
              data-testid="clear-canvas"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div 
          className="border-2 border-slate-200 rounded-xl overflow-hidden bg-white touch-none"
          style={{ maxWidth: '100%' }}
        >
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="w-full cursor-crosshair"
            style={{ touchAction: 'none' }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            data-testid="handwriting-canvas"
          />
        </div>

        {/* Submit */}
        <div className="mt-4 flex justify-center">
          <Button
            size="lg"
            className="h-12 px-8 bg-green-500 hover:bg-green-600"
            onClick={handleSubmit}
            disabled={!hasDrawn}
            data-testid="submit-writing"
          >
            <Check className="w-5 h-5 mr-2" />
            Submit Writing
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default HandwritingCanvas;
